import { useSupabaseClientStore } from "../../stores/supaBaseClientStore";
import { Helper, Person } from ".";
import { useLogger } from "../../composables/useLogger";

const logger = useLogger();

// interface UserWithRelations {
//     id: string;
//     created_at: string;
//     userconf: string;
//     person_id: string;
//     person?: Person;
//     helpers?: Array<Helper>;
// }

// interface HelperDTO {
//     id: string;
//     person_id: string;
//     firstname: string;
//     lastname: string;
//     email: string | null;
//     role: string;
// }

export class User {
  id: string;
  created_at: string;
  userconf: string;
  person_id: string;
  person: Person | null = null;
  helpers: Helper[] | null = null;

  constructor(
    id: string,
    created_at: string,
    userconf: string,
    person_id: string,
  ) {
    this.id = id;
    this.created_at = created_at;
    this.userconf = userconf;
    this.person_id = person_id;
  }

  static getSupabase() {
    const supabaseClientStore = useSupabaseClientStore();
    return supabaseClientStore.supabase;
  }

  static async fromId(userId: string) {
    const supabase = this.getSupabase();
    const { data, error } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .single();
    if (error) logger.throwSupabaseError(error, `Failed to get user with ID ${userId}`);
    return new User(data.id, data.created_at, data.userconf, data.person_id);
  }

  /**
   * For testing purposes, throw an error from the supabase client.
   */
  static async throwErrorFromSupabase(): Promise<boolean> {
    const supabase = this.getSupabase();
    const { error } = await supabase.from("users").select("*").eq("id", "1").single();
    if (error) {
      logger.throwSupabaseError(error);
    }
    return true;
  }


  async getHelpers(): Promise<Helper[]> {
    if (!this.helpers) {
      this.helpers = await Helper.getAllHelpersForUser(this.id);
    }
    return this.helpers;
  }

  async getPerson(): Promise<Person> {
    if (!this.person) {
      this.person = await Person.fromId(this.person_id);
    }
    return this.person;
  }

  static async createNewUser(personId: string) {
    const supabase = this.getSupabase();
    const { data, error } = await supabase
      .from("users")
      .insert({
        person_id: personId,
      })
      .select()
      .single();
    if (error) logger.throwSupabaseError(error, `Failed to create new user for person ${personId}`);
    return new User(data.id, data.created_at, data.userconf, data.person_id);
  }

  static async userExistsForPerson(personId: string): Promise<boolean> {
    const supabase = this.getSupabase();
    const { data } = await supabase
      .from("users")
      .select("id")
      .eq("person_id", personId)
      .maybeSingle();
    return data !== null;
  }

  static async getAllUsersWithHelpers(): Promise<User[]> {
    const supabase = this.getSupabase();
    const { data, error } = await supabase
      .from("users")
      .select(
        `
        *,
        person:persons!users_person_id_fkey (
          id,
          firstname,
          lastname,
          email
        ),
        helpers (
          id,
          person:persons!helpers_person_id_fkey (
            id,
            firstname,
            lastname,
            email
          ),
          helperroles (
            helperrole
          )
        )
      `,
      )
      .order("created_at", { ascending: false });

    if (error) logger.throwSupabaseError(error, 'Failed to get all users with helpers');

    return data.map((row) => {
      const user = new User(
        row.id,
        row.created_at,
        row.userconf,
        row.person_id,
      );

      // Add person details
      if (row.person) {
        user.person = {
          id: row.person.id,
          firstname: row.person.firstname,
          lastname: row.person.lastname,
          email: row.person.email,
        };
      }

      // Add helper details
      if (row.helpers) {
        user.helpers = row.helpers.map((helper) => ({
          id: helper.id,
          firstname: helper.person.firstname,
          lastname: helper.person.lastname,
          email: helper.person.email,
          role: helper.helperroles[0]?.helperrole || "unknown",
        }));
      }

      return user;
    });
  }

  static async deleteUserSimple(userId: string): Promise<void> {
    const supabase = this.getSupabase();
    const { error } = await supabase.from("users").delete().eq("id", userId);
    if (error) logger.throwSupabaseError(error, `Failed to delete user ${userId}`);
  }

  static async deleteUserFull(userId: string): Promise<void> {
    const supabase = this.getSupabase();
    const { useDeleteUserService } = await import(
      "../../composables/useDeleteUserService"
    );
    const deleteUserService = useDeleteUserService();

    logger.debug("Starting deleteUser in User model for userId:", userId);

    // 1. First get all related data with auth_id included
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select(
        `
        *,
        person:persons!users_person_id_fkey (
          id,
          firstname,
          lastname,
          opt_auth_id
        ),
        helpers (
          id,
          person:persons!helpers_person_id_fkey (
            id,
            firstname,
            lastname,
            opt_auth_id
          )
        )
      `,
      )
      .eq("id", userId)
      .single();

    if (userError) logger.throwSupabaseError(userError, `Failed to get user data for deletion of user ${userId}`);

    logger.debug("Retrieved user data:", userData);

    // Create a map of person IDs to their auth IDs
    const personAuthMap = new Map<string, string>();

    // Add user's person
    if (userData.person.opt_auth_id) {
      personAuthMap.set(userData.person.id, userData.person.opt_auth_id);
    }

    // Add helper persons
    userData.helpers.forEach((helper) => {
      if (helper.person.opt_auth_id) {
        personAuthMap.set(helper.person.id, helper.person.opt_auth_id);
      }
    });

    // 2. Delete helpers (cascade will handle helperroles)
    const { error: helpersError } = await supabase
      .from("helpers")
      .delete()
      .eq("user_id", userId);

    if (helpersError) logger.throwSupabaseError(helpersError, `Failed to delete helpers for user ${userId}`);

    // 3. Delete user (cascade will handle user configuration, albums, etc.)
    const { error: deleteUserError } = await supabase
      .from("users")
      .delete()
      .eq("id", userId);

    if (deleteUserError) logger.throwSupabaseError(deleteUserError, `Failed to delete user ${userId}`);

    // 4. Check and clean up persons if they're no longer referenced
    for (const [personId, authId] of personAuthMap) {
      // Check if person is still referenced by any users or helpers
      const { data: references, error: refError } = await supabase
        .from("persons")
        .select(
          `
          id,
          users!users_person_id_fkey (id),
          helpers!helpers_person_id_fkey (id)
        `,
        )
        .eq("id", personId)
        .single();

      if (refError) logger.throwSupabaseError(refError, `Failed to check references for person ${personId}`);

      // If person has no more references, delete them and their auth user
      if (references.users.length === 0 && references.helpers.length === 0) {
        logger.debug(
          "Person has no more references, deleting person and auth user:",
          { personId, authId },
        );

        // Delete the person first
        const { error: deletePersonError } = await supabase
          .from("persons")
          .delete()
          .eq("id", personId);

        if (deletePersonError) logger.throwSupabaseError(deletePersonError, `Failed to delete person ${personId}`);

        // Delete the auth user if it exists
        if (authId) {
          try {
            await deleteUserService.deleteUsers([authId]);
            logger.debug("Successfully deleted auth user:", authId);
          } catch (error) {
            logger.throwSupabaseError(error, `Failed to delete auth user ${authId}`);
          }
        }
      } else {
        logger.debug(
          "Person still has references, keeping person and auth user:",
          { personId, authId },
        );
      }
    }
  }
}
